import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import { selectUserData, selectUserToken } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { LeadsManagementEndPointLogic } from "../../../../logic/BizDevelopment/LeadsManagement/LeadsManagementEndPointLogic";
import { setItems } from "../../../../features/leadsManagementSlice";
import {
  EMPTY_AGGREGATIONS,
  Lead,
  LeadStatus,
  LeadsAggregations,
  STATUS_ORDER,
  getDefaultDateRange,
  isReportableStatus,
  mapApiAggregations,
  mapApiLeadToLead,
} from "../constants";

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const BRAND_DIRECTORY_PAGE_SIZE = 50;

function isPageSizeOption(value: number): boolean {
  return PAGE_SIZE_OPTIONS.includes(value);
}

type View = "leads" | "analytics";
type SortField =
  | "id"
  | "name"
  | "brand"
  | "status"
  | "industry"
  | "amount"
  | "date"
  | "leadScore"
  | "approvalLikelihood"
  | "qualifiedByBrand"
  | "incomeFromBrand";
type SortDirection = "asc" | "desc";

const DEFAULTS = {
  view: "leads" as View,
  status: "Pending",
  brand: "All",
  search: "",
  page: 1,
  sortDirection: "asc" as SortDirection,
};

function isSortField(value: string | null): value is SortField {
  return (
    value === "id" ||
    value === "name" ||
    value === "brand" ||
    value === "status" ||
    value === "industry" ||
    value === "amount" ||
    value === "date" ||
    value === "leadScore" ||
    value === "approvalLikelihood" ||
    value === "qualifiedByBrand" ||
    value === "incomeFromBrand"
  );
}

// Sorting stays client-side over whatever page is currently loaded — it is
// not sent to the server, unlike pagination/status/brand/search. Returns
// null for the scoring/brand-outcome fields when the value itself is null
// (not yet scored/qualified/reported) — compareLeads always sorts those to
// the end regardless of direction, rather than treating null as 0/lowest.
function getSortValue(lead: Lead, field: SortField): string | number | null {
  switch (field) {
    case "id":
      return lead.id;
    case "date":
      return new Date(lead.date).getTime();
    case "amount":
      return Number(lead.amount.replace(/[^0-9.-]/g, "")) || 0;
    case "name":
      return lead.name.toLowerCase();
    case "brand":
      return lead.brand.toLowerCase();
    case "industry":
      return lead.industry.toLowerCase();
    case "status":
      return lead.status.toLowerCase();
    case "leadScore":
      return lead.leadScore;
    case "approvalLikelihood":
      return lead.approvalLikelihood;
    case "incomeFromBrand":
      return lead.incomeFromBrand;
    case "qualifiedByBrand":
      // Not-qualified sorts before qualified (0/1) — null (still pending a
      // brand decision) is handled as "unknown" by compareLeads below, same
      // as the other nullable fields here.
      return lead.qualifiedByBrand === null ? null : lead.qualifiedByBrand ? 1 : 0;
    default:
      return "";
  }
}

function compareLeads(a: Lead, b: Lead, field: SortField, direction: SortDirection): number {
  const aValue = getSortValue(a, field);
  const bValue = getSortValue(b, field);

  // Nulls (not yet scored/qualified/reported) always sort to the end,
  // regardless of direction — clicking "descending" shouldn't surface
  // unscored leads first just because null isn't a "high" number.
  if (aValue === null || bValue === null) {
    if (aValue === null && bValue === null) return 0;
    return aValue === null ? 1 : -1;
  }

  const result =
    typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue));
  return direction === "asc" ? result : -result;
}

type ToastSeverity = "success" | "warning" | "error";

// The report endpoint reports each lead individually, so a single bulk
// request can come back with a mix of successes and failures.
function buildReportSummaryMessage(successCount: number, failedCount: number): string {
  const successText = `${successCount} lead${successCount === 1 ? "" : "s"} reported to brand successfully`;
  const failureText = `${failedCount} lead${failedCount === 1 ? "" : "s"} failed to report`;
  if (failedCount === 0) return successText;
  if (successCount === 0) return failureText;
  return `${successText}, ${failedCount} failed`;
}

function getReportToastSeverity(successCount: number, failedCount: number): ToastSeverity {
  if (failedCount === 0) return "success";
  if (successCount === 0) return "error";
  return "warning";
}

/**
 * Fetches leads from POST /partners/brand_leads_report/ for the given site.
 * Pagination, status, brand, search, qualified, min-score, and duplicate-IP
 * are all real request params — the server does the filtering/paging, this
 * hook just mirrors the current UI state into the request and reflects the
 * response back out. Every one of those params (plus page) refetches on
 * change. Column sorting stays client-side over whichever page is currently
 * loaded — the server has a documented `ordering` param but this hasn't been
 * wired up to it yet.
 *
 * Analytics' KPI cards/charts read from the response's `aggregations` object
 * (by_status/by_day/by_brand/qualified_by_brand/...), not from the paginated
 * `leads` page — so they reflect the full matching set for the current
 * brand/date filters. search/status/qualified/min-score/duplicate-IP are all
 * Leads-tab-only concepts with no Analytics-tab UI, so they're only sent to
 * the server when `view === "leads"` — otherwise they'd silently narrow
 * Analytics' aggregations too.
 *
 * view/status/brand/start/end/search/page/sort/dir/qualified/ip/min_score
 * are all mirrored to the URL query string (replacing history, not pushing)
 * so sharing the URL or refreshing the page reproduces the same view.
 */
export function useLeadsManagementData(websiteSharedUid: string) {
  const dispatch = useDispatch();
  const userToken = useSelector(selectUserToken);
  const userData = useSelector(selectUserData);
  const canReportLeads = CheckPermissions(
    userData?.data?.products?.partners,
    "lead_managment",
    "edit_lead_managment",
  );
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [searchParams, setSearchParams] = useSearchParams();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [aggregations, setAggregations] = useState<LeadsAggregations>(EMPTY_AGGREGATIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<View>(
    () => (searchParams.get("view") === "analytics" ? "analytics" : "leads"),
  );
  const [brand, setBrand] = useState(() => searchParams.get("brand") ?? DEFAULTS.brand);
  // Accumulates {brand_uid: brand_name} pairs seen across fetches so the
  // dropdown keeps showing brands the user has already filtered away from.
  // There's no dedicated "list all brands for this site" endpoint, so this
  // directory only ever knows about brands that have shown up in a response.
  const [brandDirectory, setBrandDirectory] = useState<Record<string, string>>({});
  const [start, setStart] = useState(() => searchParams.get("start") ?? defaultRange.from);
  const [end, setEnd] = useState(() => searchParams.get("end") ?? defaultRange.to);
  const [search, setSearch] = useState(() => searchParams.get("search") ?? DEFAULTS.search);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [status, setStatus] = useState<string>(() => searchParams.get("status") ?? DEFAULTS.status);
  // Qualified/IP/min-score map to the server's qualified_by_brand/
  // has_duplicate_ip/lead_scoring_min request params (Leads-tab-only, see
  // fetchLeads below). ipFilter's values are "All" | "Shared" | "Unique" —
  // has_duplicate_ip is a shared-vs-unique boolean, not a specific IP value;
  // "view leads from this exact IP" goes through the `search` param instead
  // (see goToFilteredLeads).
  const [qualified, setQualifiedState] = useState(() => searchParams.get("qualified") ?? "All");
  const [ipFilter, setIpFilterState] = useState(() => searchParams.get("ip") ?? "All");
  const [minScore, setMinScoreState] = useState(() => searchParams.get("min_score") ?? "0");
  const [sortField, setSortField] = useState<SortField | null>(() => {
    const param = searchParams.get("sort");
    return isSortField(param) ? param : null;
  });
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () => (searchParams.get("dir") === "desc" ? "desc" : DEFAULTS.sortDirection),
  );
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || DEFAULTS.page);
  const [perPage, setPerPageState] = useState(() => {
    const param = Number(searchParams.get("page_size"));
    return isPageSizeOption(param) ? param : DEFAULT_PAGE_SIZE;
  });
  const [detailId, setDetailId] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<ToastSeverity>("success");
  const [isReporting, setIsReporting] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  const fetchLeads = useCallback(() => {
    if (!userToken || !start || !end) return;
    setIsLoading(true);
    return LeadsManagementEndPointLogic.getLeadsContent(
      userToken,
      {
        website_shared_uid: [websiteSharedUid],
        date_from: start,
        date_to: end,
        page,
        page_size: perPage,
        // Analytics has no search box of its own (only Brand/Start/End), so a
        // search term typed on the Leads tab must not silently narrow its
        // aggregations too — same reasoning as report_status below.
        search: view === "leads" ? debouncedSearch || undefined : undefined,
        // Analytics always reflects every status, regardless of which status
        // tab is selected on the Leads screen — only the Leads tab itself
        // scopes the request down to a single status.
        report_status: view === "leads" && status !== "All" ? status : undefined,
        brand_uid: brand !== "All" ? [brand] : undefined,
        // qualified_by_brand/lead_scoring_min/has_duplicate_ip: proposed by
        // the backend, not yet confirmed live (see the "Extending POST
        // .../brand_leads_report/" spec's Important Notes). Omitted (not
        // null/"") when unset, matching brand_uid/report_status above.
        // Leads-tab-only, same reasoning as report_status/search.
        qualified_by_brand:
          view === "leads" && qualified !== "All" ? qualified === "Yes" : undefined,
        lead_scoring_min: view === "leads" && minScore !== "0" ? Number(minScore) : undefined,
        has_duplicate_ip:
          view === "leads" && ipFilter !== "All" ? ipFilter === "Shared" : undefined,
      },
      (data: any) => {
        const mappedLeads: Lead[] = (data?.results ?? []).map(mapApiLeadToLead);
        setLeads(mappedLeads);
        setTotalCount(typeof data?.count === "number" ? data.count : mappedLeads.length);
        setAggregations(mapApiAggregations(data?.aggregations));
        mergeBrandDirectory(mappedLeads);
        dispatch(setItems(mappedLeads));
      },
      trackPromise,
    ).finally(() => setIsLoading(false));
  }, [
    userToken,
    websiteSharedUid,
    start,
    end,
    page,
    perPage,
    debouncedSearch,
    status,
    brand,
    qualified,
    minScore,
    ipFilter,
    view,
    dispatch,
  ]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  function mergeBrandDirectory(newLeads: Lead[]) {
    setBrandDirectory((previous) => {
      const next = { ...previous };
      newLeads.forEach((lead) => {
        if (lead.brandUid) next[lead.brandUid] = lead.brand;
      });
      return next;
    });
  }

  // The Brand dropdown must always list every brand for the date range,
  // regardless of which status tab or brand is currently selected — so this
  // fetch never sends report_status/brand_uid, unlike fetchLeads above. It
  // exists purely to keep brandDirectory complete; it doesn't touch `leads`.
  useEffect(() => {
    if (!userToken || !start || !end) return;
    LeadsManagementEndPointLogic.getLeadsContent(
      userToken,
      {
        website_shared_uid: [websiteSharedUid],
        date_from: start,
        date_to: end,
        page: 1,
        page_size: BRAND_DIRECTORY_PAGE_SIZE,
        search: debouncedSearch || undefined,
      },
      (data: any) => {
        mergeBrandDirectory((data?.results ?? []).map(mapApiLeadToLead));
      },
      trackPromise,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, websiteSharedUid, start, end, debouncedSearch]);

  const brandOptions = useMemo(() => {
    const entries = Object.entries(brandDirectory).sort((a, b) => a[1].localeCompare(b[1]));
    return [
      { value: "All", label: "All brands" },
      ...entries.map(([uid, name]) => ({ value: uid, label: name })),
    ];
  }, [brandDirectory]);

  const sortedLeads = useMemo(() => {
    if (!sortField) return leads;
    return [...leads].sort((a, b) => compareLeads(a, b, sortField, sortDirection));
  }, [leads, sortField, sortDirection]);

  const totalFiltered = totalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = (currentPage - 1) * perPage;
  const pageLeads = sortedLeads;

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  // Mirror the current filters/tab/page/sort to the URL, preserving any other
  // query params already present, without piling up history entries.
  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const setOrClear = (key: string, value: string, defaultValue: string) => {
          if (value && value !== defaultValue) next.set(key, value);
          else next.delete(key);
        };
        setOrClear("view", view, DEFAULTS.view);
        setOrClear("status", status, DEFAULTS.status);
        setOrClear("brand", brand, DEFAULTS.brand);
        setOrClear("start", start, defaultRange.from);
        setOrClear("end", end, defaultRange.to);
        setOrClear("search", search, DEFAULTS.search);
        setOrClear("page", String(currentPage), String(DEFAULTS.page));
        setOrClear("page_size", String(perPage), String(DEFAULT_PAGE_SIZE));
        setOrClear("sort", sortField ?? "", "");
        setOrClear("dir", sortField ? sortDirection : "", DEFAULTS.sortDirection);
        setOrClear("qualified", qualified, "All");
        setOrClear("ip", ipFilter, "All");
        setOrClear("min_score", minScore, "0");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    status,
    brand,
    start,
    end,
    search,
    currentPage,
    perPage,
    sortField,
    sortDirection,
    qualified,
    ipFilter,
    minScore,
  ]);

  // Select-all only ever covers the current (server-paginated) page — the
  // same convention as select-all in any other paginated table.
  const reportableOnPage = useMemo(
    () => leads.filter((lead) => isReportableStatus(lead.status)),
    [leads],
  );
  const selectedCount = Object.keys(selected).length;
  const allChecked =
    reportableOnPage.length > 0 && reportableOnPage.every((lead) => selected[lead.id]);

  const detailLead = leads.find((lead) => lead.id === detailId) ?? null;

  const selectedLeads = leads.filter((lead) => selected[lead.id]);

  function goToFilteredLeads(
    overrides: Partial<{
      status: string;
      brand: string;
      start: string;
      end: string;
      minScore: number;
      qualified: string;
      search: string;
    }>,
  ) {
    setView("leads");
    setStatus(overrides.status ?? "All");
    setBrand(overrides.brand ?? "All");
    if (overrides.start) setStart(overrides.start);
    if (overrides.end) setEnd(overrides.end);
    setQualifiedState(overrides.qualified ?? "All");
    // has_duplicate_ip can't express "leads sharing this exact IP" (it's a
    // shared-vs-unique boolean, not a value filter) — "view leads from this
    // IP" goes through `search` instead, which the server matches against
    // ip_address too (see the request-param spec).
    setIpFilterState("All");
    setMinScoreState(overrides.minScore !== undefined ? String(overrides.minScore) : "0");
    setSearch(overrides.search ?? "");
    setPage(1);
    setSelected({});
    setDetailId(null);
  }

  function toggleLeadSelected(leadId: number) {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || !isReportableStatus(lead.status)) return;
    setSelected((current) => {
      const next = { ...current };
      if (next[leadId]) delete next[leadId];
      else next[leadId] = true;
      return next;
    });
  }

  function toggleAllReportable() {
    setSelected((current) => {
      const next = { ...current };
      if (allChecked) {
        reportableOnPage.forEach((lead) => delete next[lead.id]);
      } else {
        reportableOnPage.forEach((lead) => (next[lead.id] = true));
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected({});
  }

  function markLeadsReported(leadIds: number[]) {
    const reportedAt = new Date().toISOString();
    setLeads((current) =>
      current.map((lead) =>
        leadIds.includes(lead.id) ? { ...lead, status: "Success" as LeadStatus, reportedAt } : lead,
      ),
    );
  }

  async function reportSingleLead(leadId: number) {
    if (!userToken || isReporting) return;
    setIsReporting(true);
    const results = await LeadsManagementEndPointLogic.reportLeadsToBrand(
      userToken,
      [leadId],
      trackPromise,
    );
    setIsReporting(false);
    if (!results) return;
    // On a hard request failure, the error snackbar is already shown by
    // reportLeadsToBrand — keep the drawer open so the user can retry.
    const succeededIds = results.filter((result) => result.succeeded).map((result) => result.leadId);
    const failedCount = results.length - succeededIds.length;
    if (succeededIds.length > 0) markLeadsReported(succeededIds);
    setDetailId(null);
    setToastMessage(buildReportSummaryMessage(succeededIds.length, failedCount));
    setToastSeverity(getReportToastSeverity(succeededIds.length, failedCount));
    fetchLeads();
  }

  async function confirmBulkReport() {
    if (!userToken || isReporting) return;
    const reportableSelectedIds = selectedLeads
      .filter((lead) => isReportableStatus(lead.status))
      .map((lead) => lead.id);
    if (reportableSelectedIds.length === 0) {
      setShowConfirm(false);
      return;
    }
    setIsReporting(true);
    const results = await LeadsManagementEndPointLogic.reportLeadsToBrand(
      userToken,
      reportableSelectedIds,
      trackPromise,
    );
    setIsReporting(false);
    if (!results) return;
    // On a hard request failure, the error snackbar is already shown by
    // reportLeadsToBrand — keep the confirm dialog and selection intact so
    // the user can retry.
    const succeededIds = results.filter((result) => result.succeeded).map((result) => result.leadId);
    const failedCount = results.length - succeededIds.length;
    if (succeededIds.length > 0) markLeadsReported(succeededIds);
    setSelected({});
    setShowConfirm(false);
    setToastMessage(buildReportSummaryMessage(succeededIds.length, failedCount));
    setToastSeverity(getReportToastSeverity(succeededIds.length, failedCount));
    fetchLeads();
  }

  function updateFilterAndResetPage(setter: (value: string) => void) {
    return (value: string) => {
      setter(value);
      setPage(1);
      setSelected({});
    };
  }

  function setPerPage(nextPerPage: number) {
    if (!isPageSizeOption(nextPerPage)) return;
    setPerPageState(nextPerPage);
    setPage(1);
    setSelected({});
  }

  return {
    leads,
    isLoading,
    canReportLeads,
    refetch: fetchLeads,
    view,
    setView,
    brand,
    start,
    end,
    search,
    status,
    sortField,
    sortDirection,
    toggleSort,
    page: currentPage,
    totalPages,
    perPage,
    setPerPage,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    pageStartIndex,
    totalFiltered,
    pageLeads,
    leadsForStatus: leads,
    leadsForAnalytics: leads,
    aggregations,
    allChecked,
    selectedCount,
    selectedLeads,
    detailLead,
    showConfirm,
    isReporting,
    toastMessage,
    toastSeverity,
    clearToast: () => setToastMessage(""),
    selected,
    brandOptions,
    statusOrder: STATUS_ORDER,
    qualified,
    ipFilter,
    minScore,
    setBrand: updateFilterAndResetPage(setBrand),
    setStart: updateFilterAndResetPage(setStart),
    setEnd: updateFilterAndResetPage(setEnd),
    setSearch: updateFilterAndResetPage(setSearch),
    setQualified: updateFilterAndResetPage(setQualifiedState),
    setIpFilter: updateFilterAndResetPage(setIpFilterState),
    setMinScore: updateFilterAndResetPage(setMinScoreState),
    setStatus: (nextStatus: string) => {
      setStatus(nextStatus);
      setPage(1);
      setSelected({});
    },
    goToFilteredLeads,
    toggleLeadSelected,
    toggleAllReportable,
    clearSelection,
    openLeadDetail: (leadId: number) => setDetailId(leadId),
    closeLeadDetail: () => setDetailId(null),
    reportSingleLead,
    requestBulkReport: () => setShowConfirm(true),
    cancelBulkReport: () => setShowConfirm(false),
    confirmBulkReport,
    goToPrevPage: () => setPage((current) => Math.max(1, current - 1)),
    goToNextPage: () => setPage((current) => Math.min(totalPages, current + 1)),
  };
}

export type LeadsManagementData = ReturnType<typeof useLeadsManagementData>;
